<template>
  <aside
    :class="mobileOpen ? 'fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] rounded-t-2xl border-t' : 'hidden'"
    :style="mobileOpen ? { display: 'flex', position: 'fixed', top: 'auto', right: '0', bottom: '0', left: '0' } : undefined"
    class="global-sidebar relative w-full shrink-0 flex-col border-b border-zinc-200 bg-white px-4 py-4 lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:min-h-screen lg:w-64 lg:overflow-hidden lg:rounded-none lg:border-b-0 lg:border-r lg:py-5"
  >
    <div class="mb-3 flex items-center justify-between lg:hidden">
      <span class="px-2 text-sm font-semibold text-zinc-950">Navegação</span>
      <button type="button" class="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950" aria-label="Fechar menu de navegação" @click="closeMobileSidebar">
        <X class="h-5 w-5" />
      </button>
    </div>
    <Link href="/" class="mb-5 hidden items-center gap-3 px-2 lg:mb-8 lg:flex">
      <img
        :src="logoStacklab"
      class="h-10 w-auto object-contain brightness-0 lg:h-14"
        alt="Kanban para Chatwoot Logo"
      />
      <span class="text-lg font-semibold tracking-tight text-zinc-500">Docs</span>
    </Link>
    <p class="-mt-2 mb-4 hidden px-2 text-xs leading-5 text-zinc-400 lg:-mt-4 lg:mb-6 lg:block">
      Documentação das soluções digitais da StackLab
    </p>

    <nav ref="navElement" class="global-sidebar-nav flex max-h-[60vh] flex-col gap-1 overflow-y-auto pb-4 lg:min-h-0 lg:max-h-none lg:flex-1 lg:overflow-y-auto lg:pb-20" aria-label="Navegação principal">
      <Link
        href="/"
        class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100"
        :class="isActive('/') ? 'bg-zinc-100' : ''"
        :aria-current="isActive('/') ? 'page' : undefined"
      >
        <House weight="duotone" class="h-4 w-4 text-zinc-700" aria-hidden="true" />
        <span>Visão Geral</span>
      </Link>
      <Link
        href="/geradores"
        class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        :class="isActive('/geradores') ? 'bg-zinc-100 text-zinc-900' : ''"
        :aria-current="isActive('/geradores') ? 'page' : undefined"
      >
        <Stack class="h-4 w-4 text-zinc-700" weight="duotone" aria-hidden="true" />
        <span>Gerador de stack</span>
      </Link>
      <div class="mb-1 mt-6 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        <BookOpen weight="duotone" class="h-3.5 w-3.5" aria-hidden="true" />
        <span>Documentações de API</span>
      </div>
      <Link
        href="/kanban"
        class="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        :class="isActive('/kanban') ? 'bg-zinc-100 text-zinc-900' : ''"
        :aria-current="isActive('/kanban') ? 'page' : undefined"
      >
        <span>Kanban para Chatwoot</span>
        <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400">v2.9.6</span>
      </Link>
      <Link
        href="/stackzap"
        class="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        :class="isActive('/stackzap') ? 'bg-zinc-100 text-zinc-900' : ''"
        :aria-current="isActive('/stackzap') ? 'page' : undefined"
      >
        <span>StackZap API</span>
        <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400">v0.1.1</span>
      </Link>
      <div class="mb-1 mt-6 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        <GraduationCap weight="duotone" class="h-3.5 w-3.5" aria-hidden="true" />
        <span>Manuais</span>
      </div>
      <Link
        href="/manuais"
        class="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        :class="isActive('/manuais') ? 'bg-zinc-100 text-zinc-900' : ''"
        :aria-current="isActive('/manuais') ? 'page' : undefined"
      >
        <span>Todos os manuais</span>
        <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400">{{ totalManuals }}</span>
      </Link>
      <details class="group mt-1" :open="page.url.startsWith('/manuais/categoria/')">
        <summary class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 [&::-webkit-details-marker]:hidden">
          <span>Categorias</span>
          <SquaresFour weight="duotone" class="h-4 w-4 text-zinc-700" aria-hidden="true" />
        </summary>
        <div class="ml-3 border-l border-zinc-200 pl-3">
          <Link v-for="category in manualCategories" :key="category.slug" :href="`/manuais/categoria/${category.slug}`" class="flex items-center justify-between gap-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isActive(`/manuais/categoria/${category.slug}`) ? 'font-medium text-zinc-950' : ''">
            <span class="truncate">{{ category.name }}</span>
            <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400">{{ categoryCount(category.slug) }}</span>
          </Link>
        </div>
      </details>
      <div class="mt-1 space-y-1">
        <details
          v-for="solution in manualCatalog"
          :key="solution.slug"
          :open="solution.manuals.some((manual) => isActive(`/manuais/${manual.slug}`))"
          class="group"
        >
          <summary class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 [&::-webkit-details-marker]:hidden">
            <span class="truncate">{{ solution.name }}</span>
            <CaretDown class="h-4 w-4 shrink-0 text-zinc-400 transition group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div class="ml-3 border-l border-zinc-200 pl-3">
            <Link
              v-for="manual in solution.manuals"
              :key="manual.slug"
              :href="`/manuais/${manual.slug}`"
              class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950"
              :class="isActive(`/manuais/${manual.slug}`) ? 'font-medium text-zinc-950' : ''"
            >
              {{ manual.title }}
            </Link>
          </div>
        </details>
      </div>
      <div class="mb-1 mt-6 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        <Question weight="duotone" class="h-3.5 w-3.5" aria-hidden="true" />
        <span>Dúvidas frequentes</span>
      </div>
      <details class="group mt-1" :open="page.url.startsWith('/duvidas-frequentes')">
        <summary class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 [&::-webkit-details-marker]:hidden">
          <span>Problemas comuns</span>
          <CaretDown class="h-4 w-4 shrink-0 text-zinc-400 transition group-open:rotate-180" aria-hidden="true" />
        </summary>
        <div class="ml-3 border-l border-zinc-200 pl-3">
          <a href="/duvidas-frequentes#atualizacao-sem-funcoes" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('atualizacao-sem-funcoes') ? 'font-medium text-zinc-950' : ''">
            Atualizei o Kanban, mas não recebi as novas funções
          </a>
          <a href="/duvidas-frequentes#erro-500-api" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('erro-500-api') ? 'font-medium text-zinc-950' : ''">
            Recebi erro 500 ao usar a API do Kanban
          </a>
          <a href="/duvidas-frequentes#funcoes-nao-aparecem" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('funcoes-nao-aparecem') ? 'font-medium text-zinc-950' : ''">
            Instalei, mas as funções não aparecem
          </a>
          <a href="/duvidas-frequentes#perdi-logo-atualizacao" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('perdi-logo-atualizacao') ? 'font-medium text-zinc-950' : ''">
            Perdi o logotipo personalizado na atualização
          </a>
        </div>
      </details>
      <div class="mb-1 mt-5 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        <Lightbulb weight="duotone" class="h-3.5 w-3.5" aria-hidden="true" />
        <span>Principais dúvidas</span>
      </div>
      <details class="group mt-1" :open="page.url === '/duvidas-frequentes#desativar-kanban-conta'">
        <summary class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 [&::-webkit-details-marker]:hidden">
          <span>Kanban nas contas</span>
          <CaretDown class="h-4 w-4 shrink-0 text-zinc-400 transition group-open:rotate-180" aria-hidden="true" />
        </summary>
        <div class="ml-3 border-l border-zinc-200 pl-3">
          <a href="/duvidas-frequentes#desativar-kanban-conta" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('desativar-kanban-conta') ? 'font-medium text-zinc-950' : ''">
            Posso desativar o Kanban em uma conta?
          </a>
          <a href="/duvidas-frequentes#itens-separados-por-conta" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('itens-separados-por-conta') ? 'font-medium text-zinc-950' : ''">
            Uma conta vê os itens de outra conta?
          </a>
          <a href="/duvidas-frequentes#limitar-agentes-kanban" class="block py-1.5 text-xs text-zinc-500 hover:text-zinc-950" :class="isFaqActive('limitar-agentes-kanban') ? 'font-medium text-zinc-950' : ''">
            Posso limitar agentes por Kanban?
          </a>
        </div>
      </details>
    </nav>

    <div v-if="showSidebarFade" class="pointer-events-none fixed bottom-0 left-0 z-10 hidden h-16 w-[calc(16rem-1px)] bg-gradient-to-t from-white via-white/90 to-transparent lg:block" aria-hidden="true"></div>
  </aside>
  <div v-if="mobileOpen" class="fixed inset-0 z-40 bg-zinc-950/30 lg:hidden" aria-hidden="true" @click="closeMobileSidebar"></div>
</template>

<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { PhBookOpen as BookOpen, PhCaretDown as CaretDown, PhGraduationCap as GraduationCap, PhHouse as House, PhLightbulb as Lightbulb, PhQuestion as Question, PhSquaresFour as SquaresFour, PhStack as Stack, PhX as X } from "@phosphor-icons/vue";
import logoStacklab from "../../assets/logo_stacklab.png";
import { getManualCategory, manualCategories, manualCatalog } from "../../data/manualCatalog";

const page = usePage();
const navElement = ref<HTMLElement | null>(null);
const showSidebarFade = ref(false);
const mobileOpen = ref(false);
const faqHash = ref("");
const isActive = (path: string) => page.url === path;
const isFaqActive = (id: string) => page.url.startsWith("/duvidas-frequentes") && faqHash.value === `#${id}`;
const updateFaqHash = () => { faqHash.value = window.location.hash; };
const totalManuals = computed(() => manualCatalog.reduce((total, solution) => total + solution.manuals.length, 0));
const categoryCount = (slug: string) => manualCatalog.reduce((total, solution) => total + solution.manuals.filter((manual) => getManualCategory(manual) === slug).length, 0);
const updateSidebarFade = () => { showSidebarFade.value = !!navElement.value && navElement.value.scrollHeight > navElement.value.clientHeight; };
const closeMobileSidebar = () => { mobileOpen.value = false; };
const toggleMobileSidebar = () => { mobileOpen.value = !mobileOpen.value; };
let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;
onMounted(async () => {
  updateFaqHash();
  window.addEventListener("toggle-mobile-sidebar", toggleMobileSidebar);
  window.addEventListener("hashchange", updateFaqHash);
  await nextTick();
  updateSidebarFade();
  window.addEventListener("resize", updateSidebarFade);
  if (navElement.value) {
    resizeObserver = new ResizeObserver(updateSidebarFade);
    resizeObserver.observe(navElement.value);
    mutationObserver = new MutationObserver(updateSidebarFade);
    mutationObserver.observe(navElement.value, { childList: true, subtree: true, attributes: true, attributeFilter: ["open"] });
  }
});
onUnmounted(() => {
  window.removeEventListener("toggle-mobile-sidebar", toggleMobileSidebar);
  window.removeEventListener("hashchange", updateFaqHash);
  window.removeEventListener("resize", updateSidebarFade);
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});
</script>

<script lang="ts">
export default { name: "Sidebar" };
</script>

<style scoped>
.global-sidebar {
  scrollbar-width: none;
}

.global-sidebar::-webkit-scrollbar {
  display: none;
}

.global-sidebar-nav {
  scrollbar-width: none;
}

.global-sidebar-nav::-webkit-scrollbar {
  display: none;
}
</style>
